import React, { useMemo } from "react";
import {
    GridRenderEditCellParams,
    GridSingleSelectColDef,
    useGridApiContext,
} from "@mui/x-data-grid-premium";
import {
    AutocompleteOption,
    AutocompleteValue,
    AutocompleteWrapper,
} from "../muiAutocomplete";
import { useTranslation } from "react-i18next";

interface MuiDataGridAutoCompleteProps extends GridRenderEditCellParams {
    multiple?: boolean;
}

export const MuiDataGridAutoComplete = ({
    id,
    field,
    value,
    hasFocus,
    colDef,
    multiple,
}: MuiDataGridAutoCompleteProps) => {
    const apiRef = useGridApiContext();
    const { valueOptions } = colDef as GridSingleSelectColDef;
    const { t } = useTranslation();

    const handleValueChange = async (newValue: string | string[] | null) => {
        await apiRef.current.setEditCellValue({
            id,
            field,
            value: newValue,
        });
    };

    const options: AutocompleteOption[] = useMemo(
        () =>
            (valueOptions as { value: string; label: string }[])?.map((o) => ({
                id: o.value,
                title: o.label,
            })) ?? [],
        [valueOptions]
    );

    const handleChange = (val: AutocompleteValue | null) => {
        if (multiple) {
            const selected = (val as AutocompleteOption[]) ?? [];
            void handleValueChange(selected.map((v) => v.id));
        } else {
            void handleValueChange((val as AutocompleteOption)?.id ?? null);
        }
    };

    return (
        <AutocompleteWrapper
            options={options}
            size="small"
            multiple={multiple}
            defaultOpen={hasFocus}
            value={value ?? (multiple ? [] : undefined)}
            placeholder={t("common.typeToSeeOptions")}
            onChange={handleChange}
            fullWidth
            textFieldProps={{
                size: "small",
                variant: "standard",
                sx: { fontSize: "0.875rem" },
            }}
        />
    );
};
